// Card-composition heuristics for the compact (mobile-style) transaction list.
//
// A port of the Android runtime's `TransactionListRenderer.kt`: the transaction
// model carries no explicit "title" / "badge" / "chip" fields, so the compact
// card derives them positionally + by shape from the list columns — an ID badge
// (first id-looking column), a bold title (the first remaining column), and a
// category chip (an accounting-nature / *category value). Everything else
// becomes the label/value detail grid. Kept as pure, unit-testable functions so
// the presentation component (CompactRecordCard) stays declarative.

import type { ColumnDef, SqlModel } from '../types'
import { columnDbName, columnFormKey, listOrderedColumns, richCellText } from '../utils'
import { temporalCellText } from '../displayFormat'

// Accounting-nature values that read as a "category" chip (matches the Android
// set). Case-insensitive.
const CATEGORY_NATURES = new Set([
  'assets', 'asset', 'liabilities', 'liability',
  'income', 'revenue', 'expenses', 'expense',
])

// A column whose NAME reads like a record identifier — `id`, `*_id`, `* id`, or
// a heading of exactly "id". Used to pick the leading ID badge.
export function looksLikeId(col: ColumnDef): boolean {
  const n = (col.name || col.db_name || '').trim().toLowerCase()
  const l = (col.heading || '').trim().toLowerCase()
  return n === 'id' || n.endsWith('_id') || n.endsWith(' id') || l === 'id'
}

// A column that reads like a category: its VALUE is a known accounting nature,
// or its NAME contains `nature` / `category`. A blank value never qualifies on
// the value test (name test still applies).
export function looksLikeCategory(col: ColumnDef, value: string | null): boolean {
  const v = (value ?? '').trim().toLowerCase()
  if (v && CATEGORY_NATURES.has(v)) return true
  const n = (col.name || col.db_name || '').trim().toLowerCase()
  return n.includes('nature') || n.includes('category')
}

// Columns shown on the compact card, in order: the designer-configured
// `list_view.columns` (matched by name or db_name, case-insensitive) when set,
// else the first four visible columns in grid (x) order — the same ordering the
// desktop list uses, so the two views stay visually consistent.
export function compactListColumns(sm: SqlModel): ColumnDef[] {
  const visible = listOrderedColumns(sm)
  const configured = (sm.list_view?.columns || [])
    .map(s => String(s ?? '').trim().toLowerCase())
    .filter(Boolean)
  if (configured.length) {
    const byKey = new Map<string, ColumnDef>()
    for (const c of visible) {
      const nm = (c.name || '').trim().toLowerCase()
      if (nm && !byKey.has(nm)) byKey.set(nm, c)
      const db = columnDbName(c)
      if (db && !byKey.has(db)) byKey.set(db, c)
    }
    const resolved: ColumnDef[] = []
    const seen = new Set<ColumnDef>()
    for (const name of configured) {
      const c = byKey.get(name)
      if (c && !seen.has(c)) { resolved.push(c); seen.add(c) }
    }
    if (resolved.length) return resolved
  }
  return visible.slice(0, 4)
}

// The raw (unformatted) value for a column out of a list row. Transaction rows
// come back keyed by db_name (what the desktop grid reads), but we probe a few
// key shapes so the lookup is robust to name/db_name differences and casing.
export function compactRawValue(
  row: Record<string, unknown>, col?: ColumnDef,
): unknown {
  if (!col) return null
  const keys = [
    columnDbName(col), columnFormKey(col),
    col.name, col.db_name,
    (col.name || '').toLowerCase(), (col.db_name || '').toLowerCase(),
  ]
  for (const k of keys) {
    if (!k) continue
    const v = row[k]
    if (v != null && v !== '') return v
  }
  return null
}

// The string form of a column's value (for the ID badge / title / detail grid
// when no display format applies). Null when blank.
export function compactCellValue(
  row: Record<string, unknown>, col?: ColumnDef,
): string | null {
  const v = compactRawValue(row, col)
  if (v == null || v === '') return null
  const rich = richCellText(v, col)
  if (rich != null) return rich || null
  const when = temporalCellText(v, col)
  if (when != null) return when
  return typeof v === 'string' ? v : String(v)
}

export interface CardParts {
  idCol?: ColumnDef
  titleCol?: ColumnDef
  catCol?: ColumnDef
  detailCols: ColumnDef[]
}

// Split a row's compact columns into the card's structural parts: the leading
// ID badge (first id-looking column with a non-blank value), the category chip
// (first remaining category-looking column), the bold title (the first column
// left, falling back to the very first column when everything was consumed),
// and the remaining columns as the label/value detail grid.
export function cardParts(sm: SqlModel, row: Record<string, unknown>): CardParts {
  const cols = compactListColumns(sm)
  const remaining = [...cols]

  let idCol: ColumnDef | undefined
  for (let i = 0; i < remaining.length; i++) {
    const c = remaining[i]
    if (looksLikeId(c) && compactCellValue(row, c) != null) {
      idCol = c
      remaining.splice(i, 1)
      break
    }
  }

  let catCol: ColumnDef | undefined
  for (let i = 0; i < remaining.length; i++) {
    const c = remaining[i]
    if (looksLikeCategory(c, compactCellValue(row, c))) {
      catCol = c
      remaining.splice(i, 1)
      break
    }
  }

  const titleCol = remaining.shift() || cols[0]
  return { idCol, titleCol, catCol, detailCols: remaining }
}
